import { loadImage } from './rasterizeSvg';
import { t } from '../../../i18n';
import c4Icon from '../../../assets/icons/c4.svg';
import flashedIcon from '../../../assets/icons/flashed_kill.svg';
import noscopeIcon from '../../../assets/icons/noscope.svg';
import smokeIcon from '../../../assets/icons/smoke_kill.svg';
import wallbangIcon from '../../../assets/icons/wallbang.svg';
import headshotIcon from '../../../assets/icons/headshot.svg';

// What ViewerHud.vue shows, drawn in canvas 2D.
// Sizes and colours follow ViewerHud.vue and tailwind.config.js.
const COLORS = {
  primary: '#F8F9FB',
  secondary: '#9CADC4',
  danger: '#EF4444',
  CT: '#6398E7',
  T: '#F7A94C',
  rowBackground: 'rgba(17, 24, 39, 0.8)',
};
const FONT = 'Inter, sans-serif';

// A missing icon leaves a gap in the row instead of failing the screenshot.
const tryLoad = (src) => loadImage(src).catch(() => null);

function killItems(kill) {
  const icon = (src, height) => ({ src, height });
  const name = (text, side) => ({ text, color: side === 'CT' ? COLORS.CT : COLORS.T });
  return [
    kill.attackerBlinded && icon(flashedIcon, 16),
    name(kill.attackerName, kill.attackerSide),
    icon(`/img/icons/${kill.weapon}.png`, 12),
    kill.noScope && icon(noscopeIcon, 16),
    kill.thruSmoke && icon(smokeIcon, 16),
    kill.isWallbang && icon(wallbangIcon, 16),
    kill.isHeadshot && icon(headshotIcon, 16),
    name(kill.victimName, kill.victimSide),
  ].filter(Boolean);
}

async function loadItems(items) {
  return Promise.all(items.map(async (item) => (
    item.src ? { ...item, image: await tryLoad(item.src) } : item
  )));
}

function drawTimer(ctx, {
  round, timerText, isBombTimer, c4, centre, px,
}) {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = COLORS.secondary;
  ctx.font = `${px(12)}px ${FONT}`;
  ctx.fillText(t('shared.demo_viewer.round', { round }), centre, px(24));

  ctx.font = `600 ${px(16)}px ${FONT}`;
  ctx.fillStyle = isBombTimer ? COLORS.danger : COLORS.primary;
  const top = px(46);
  if (isBombTimer && c4) {
    const iconWidth = (px(20) * c4.naturalWidth) / c4.naturalHeight;
    const textWidth = ctx.measureText(timerText).width;
    const left = centre - (iconWidth + px(4) + textWidth) / 2;
    ctx.drawImage(c4, left, top, iconWidth, px(20));
    ctx.textAlign = 'left';
    ctx.fillText(timerText, left + iconWidth + px(4), top + px(2));
  } else {
    ctx.fillText(timerText, centre, top + px(2));
  }
}

function drawKillRow(ctx, items, { right, top, px }) {
  const gap = px(12);
  const padX = px(12);
  const height = px(36);
  ctx.font = `${px(14)}px ${FONT}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  const sized = items
    .filter((item) => item.text !== undefined || item.image)
    .map((item) => {
      if (item.image) {
        const h = px(item.height);
        return { ...item, h, w: (h * item.image.naturalWidth) / item.image.naturalHeight };
      }
      return { ...item, w: ctx.measureText(String(item.text ?? '')).width };
    });
  const width = sized.reduce((sum, item) => sum + item.w, 0) + gap * (sized.length - 1) + padX * 2;

  ctx.fillStyle = COLORS.rowBackground;
  ctx.fillRect(right - width, top, width, height);

  const middle = top + height / 2;
  sized.reduce((x, item) => {
    if (item.image) {
      ctx.drawImage(item.image, x, middle - item.h / 2, item.w, item.h);
    } else {
      ctx.fillStyle = item.color;
      ctx.fillText(String(item.text ?? ''), x, middle);
    }
    return x + item.w + gap;
  }, right - width + padX);
}

// eslint-disable-next-line import-x/prefer-default-export
export async function drawHud(ctx, {
  round, timerText, isBombTimer, kills = [], width, scale = 1,
}) {
  const px = (n) => n * scale;
  const [c4, rows] = await Promise.all([
    isBombTimer ? tryLoad(c4Icon) : null,
    Promise.all(kills.map((kill) => loadItems(killItems(kill)))),
  ]);

  ctx.save();
  drawTimer(ctx, {
    round, timerText, isBombTimer, c4, centre: (width * scale) / 2, px,
  });
  rows.forEach((items, i) => {
    drawKillRow(ctx, items, { right: px(width - 24), top: px(24 + i * 44), px });
  });
  ctx.restore();
}
